import { Injectable } from "@angular/core";
import JSZip from "jszip";
import mime from "mime";
import { replaceFileExtensionConditionally } from "../global-functions/global-functions";

@Injectable({ providedIn: "root" })
export class DownloadService {
  createLinkAndDownloadFile(file: Blob, filename) {
    const downloadAnchor = document.createElement("a");
    downloadAnchor.style.display = "none";

    downloadAnchor.href = this.getDownloadedFileUrl(file);
    downloadAnchor.download = filename;
    downloadAnchor.click();
  }

  getDownloadedFileUrl(file: Blob): string {
    return URL.createObjectURL(file);
  }

  private isValidBase64 = (str: string): boolean => {
    try {
      return btoa(atob(str)) === str;
    } catch (err) {
      return false;
    }
  };

  private normaliseBase64 = (str: string): string => str.replace(/-/g, "+").replace(/_/g, "/");

  private base64ToUint8Array = (base64: string): Uint8Array => {
    const cleaned = this.isValidBase64(base64) ? base64 : this.normaliseBase64(base64);
    const data = atob(cleaned);
    const uint8Array = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      uint8Array[i] = data.charCodeAt(i);
    }
    return uint8Array;
  };

  private ensureZipName = (zipName: string): string => {
    const name = replaceFileExtensionConditionally(zipName ?? "");
    return name.toLowerCase().endsWith(".zip") ? name : `${name}.zip`;
  };

  async createAndDownloadZip(files: { base64: string; name: string }[], zipName: string): Promise<void> {
    const zip = new JSZip();

    for (const file of files) {
      const bytes = this.base64ToUint8Array(file.base64);
      zip.file(file.name, bytes);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    this.createLinkAndDownloadFile(zipBlob, this.ensureZipName(zipName));
  }

  getDownloadedFileBlobByString(fileDataString: string, rawFileName: string): Blob {
    const fileName = this.getFileNameFromPath(rawFileName);
    let data: string;

    if (!this.isValidBase64(fileDataString)) {
      data = atob(fileDataString.replace(/-/g, "+").replace(/_/g, "/"));
    } else {
      data = atob(fileDataString);
    }

    const uint8Array = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      uint8Array[i] = data.charCodeAt(i);
    }

    return new Blob([uint8Array], {
      type: this.getFileMimeType(fileName),
    });
  }

  handleDownloadedFile(fileDataString: string, rawFileName: string) {
    const fileName = replaceFileExtensionConditionally(this.getFileNameFromPath(rawFileName));
    const blob = this.getDownloadedFileBlobByString(fileDataString, rawFileName);

    this.createLinkAndDownloadFile(blob, fileName);
  }

  getFileMimeType(filename: string): string | undefined {
    const extension = filename.split(".").pop()?.toLowerCase();
    if (!extension) return undefined;

    const mimeTypeFromExtension = mime.getType(extension);
    if (mimeTypeFromExtension) {
      return mimeTypeFromExtension;
    }
    return "application/octet-stream";
  }

  isImageFile(type: string): boolean {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];

    if (!type) {
      return false;
    }

    const fileExtension = type.split(".").pop()?.toLowerCase();
    return !!fileExtension && imageExtensions.includes(fileExtension);
  }

  isEmlFile(fileName: string): boolean {
    if (!fileName) {
      return false;
    }

    const fileExtension = replaceFileExtensionConditionally(fileName).split(".").pop()?.toLowerCase();
    return !!fileExtension && fileExtension === "eml";
  }

  getFileNameFromPath(path: string): string {
    return path?.split("\\").pop() ?? "";
  }

  async blobToDataURI(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read Blob as data URI"));
        }
      };
      reader.onerror = () => {
        reject(new Error("Failed to read Blob as data URI"));
      };
      reader.readAsDataURL(blob);
    });
  }

  blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.readAsDataURL(blob);
    });

  dataUriToBase64(dataUri: string): string {
    return dataUri.slice(dataUri.indexOf(",") + 1);
  }
}
